import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import { Select, Input, Button, Checkbox, DatePicker, Switch, AutoComplete, message, Modal } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";
import { apiFetch, apiUrl } from '../api';
import { getLocalDateString } from '../utils/date';
import PhoneInputPkg from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getTranslatedLabel, getCompanyLabels, isEmployeeRole } from '../utils/companyRoles';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = "Asia/Kolkata";


const PhoneInput = PhoneInputPkg.default || PhoneInputPkg;

const FIELD_TYPES = {
  textbox: 'textbox',
  dropdown: 'dropdown',
  checklist: 'checklist',
  date: 'date',
  datetime: 'datetime',
};

const CORE_FIELD_KEYS = new Set(['firstName', 'lastName', 'email', 'mobileNumber', 'hostId']);

const isCoreField = (field) => CORE_FIELD_KEYS.has(field.key);

const buildDefaultValue = (field) => (field.type === FIELD_TYPES.checklist ? [] : '');

const normalizeOptions = (options) =>
  (Array.isArray(options) ? options : []).map((option) => ({
    value: option,
    label: option
  }));

const createFallbackSection = (title = 'Additional Details') => ({
  id: 'additional-details',
  title,
});

const VISITOR_PHOTO_BOX_CLASS =
  'w-[min(100%,14rem)] sm:w-56 aspect-square rounded-2xl object-cover border border-slate-200 dark:border-slate-700';

const RegisterVisitor = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{"role": "admin"}');
  const userRole = user.role?.toLowerCase() || '';
  const labels = getCompanyLabels(user?.companyName);
  const isReceptionist = userRole === 'receptionist';
  const isStaffRegistrar = userRole === 'admin' || isEmployeeRole(userRole);
  const [step, setStep] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [formSections, setFormSections] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [visitorNameSuggestions, setVisitorNameSuggestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [optionalSectionExpanded, setOptionalSectionExpanded] = useState({});

  // Smart Host Selector state
  const [hostSearchText, setHostSearchText] = useState('');
  const [hostSearchResults, setHostSearchResults] = useState([]);
  const [hostSearchLoading, setHostSearchLoading] = useState(false);
  const [hostSearchPage, setHostSearchPage] = useState(1);
  const [hostSearchHasMore, setHostSearchHasMore] = useState(true);
  const [selectedHostLabel, setSelectedHostLabel] = useState('');
  const hostSearchTimerRef = useRef(null);

  // Webcam state
  const [photo, setPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const streamRef = useRef(null);
  const videoNodeRef = useRef(null); // holds the actual DOM node for capture
  const lookupAbortRef = useRef(null);
  const lastLookupKeyRef = useRef('');

  // Callback ref — fires the instant the <video> element mounts, guaranteeing srcObject is set
  const videoRef = useCallback((node) => {
    videoNodeRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      setCameraActive(true);
    } catch {
      message.warning('Unable to access camera. Please grant camera permissions.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
    setVideoReady(false);
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoNodeRef.current;
    if (!video) return;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    setPhoto(canvas.toDataURL('image/jpeg', 0.8));
    setErrors((currentErrors) => {
      if (!currentErrors.photo) return currentErrors;
      const updatedErrors = { ...currentErrors };
      delete updatedErrors.photo;
      return updatedErrors;
    });
    stopCamera();
  }, [stopCamera]);

  const retakePhoto = useCallback(() => {
    setPhoto(null);
    setVideoReady(false);
    startCamera();
  }, [startCamera]);

  // Clean up camera stream when component unmounts
  useEffect(() => () => stopCamera(), [stopCamera]);

  const createInitialFormData = () => ({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    hostId: "",
  });

  const [formData, setFormData] = useState(createInitialFormData);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const applyVisitorFromLookup = (visitor) => {
    if (!visitor) return;

    if (isReceptionist && visitor.activePreApprovedVisit) {
      Modal.confirm({
        title: 'Visitor Already Pre-approved',
        content: 'This visitor is already pre-approved. You should check them in from the Visit Requests page instead of registering them again.',
        okText: 'Go to Check In',
        cancelText: 'Cancel & Clear',
        onOk() {
          navigate('/visit-requests?tab=Pre-approved+visitor');
        },
        onCancel() {
          setFormData(createInitialFormData());
          setCustomFieldValues({});
        }
      });
      return;
    }

    setFormData((currentData) => ({
      ...currentData,
      firstName: visitor.firstName || currentData.firstName,
      lastName: visitor.lastName || currentData.lastName,
      email: visitor.email || currentData.email,
      mobileNumber: visitor.mobileNumber || currentData.mobileNumber,
      hostId: visitor.hostId || currentData.hostId,
    }));

        if (visitor.customFields && typeof visitor.customFields === 'object') {
          setCustomFieldValues((currentValues) => {
            const nextValues = { ...currentValues };
            Object.entries(visitor.customFields).forEach(([key, value]) => {
              const fieldDef = formFields.find(f => f.key === key);
              const isDateField = fieldDef && (fieldDef.type === FIELD_TYPES.datetime || fieldDef.type === FIELD_TYPES.date);
              if (key !== 'expectedDate' && !isDateField && value !== undefined && value !== null) {
                nextValues[key] = value;
              }
            });
            if (visitor.lastName) nextValues.lastName = visitor.lastName;
            if (visitor.purpose) {
              nextValues.purpose = visitor.purpose;
              nextValues.__purpose_select__ = visitor.purpose;
            }
            if (visitor.address?.line1) nextValues.addr_line1 = visitor.address.line1;
            if (visitor.address?.city) nextValues.addr_city = visitor.address.city;
            if (visitor.address?.state) nextValues.addr_state = visitor.address.state;
            return nextValues;
          });
        }

    if (isReceptionist && visitor.photo) {
      setPhoto(visitor.photo);
      clearFieldError('photo');
    }
  };

  const searchPastVisitors = async (query) => {
    if (!query) {
      setVisitorNameSuggestions([]);
      return;
    }
    try {
      const res = await apiFetch(`/api/visitors/search-names?q=${encodeURIComponent(query)}`);
      const payload = await res.json();
      if (res.ok && payload.success) {
        setVisitorNameSuggestions(payload.data.map(v => ({ value: v.fullName, data: v })));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const lookupVisitorByContact = async (email, mobileNumber) => {
    const normalizedEmail = email?.trim();
    const normalizedMobile = mobileNumber?.trim();
    const lookupKey = normalizedEmail
      ? `email:${normalizedEmail.toLowerCase()}`
      : normalizedMobile
        ? `mobile:${normalizedMobile}`
        : '';

    if (!lookupKey || lookupKey === lastLookupKeyRef.current) return;
    if (normalizedEmail && !normalizedEmail.includes('@')) return;
    if (!normalizedEmail && normalizedMobile.length < 8) return;

    lookupAbortRef.current?.abort?.();
    const controller = new AbortController();
    lookupAbortRef.current = controller;

    try {
      const params = new URLSearchParams();
      if (normalizedEmail) params.set('email', normalizedEmail);
      else params.set('mobileNumber', normalizedMobile);

      const response = await apiFetch(`/api/visitors/lookup?${params.toString()}`, {
        signal: controller.signal,
      });
      const payload = await response.json();
      if (!response.ok || !payload.data) return;

      lastLookupKeyRef.current = lookupKey;
      applyVisitorFromLookup(payload.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Visitor lookup failed:', error);
      }
    }
  };

  // Build a display label for a host employee
  const buildHostLabel = useCallback((emp) => {
    return `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
  }, []);

  // Smart Host Search with debounce
  const searchHosts = useCallback(async (searchText, page = 1) => {
    setHostSearchLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page,
        limit: 20
      });
      if (searchText?.trim()) {
        queryParams.append('query', searchText.trim());
      }
      
      const response = await apiFetch(`/api/employees/search?${queryParams.toString()}`);
      const data = await response.json();
      const results = Array.isArray(data.data) ? data.data : [];
      
      if (page === 1) {
        setHostSearchResults(results);
      } else {
        setHostSearchResults(prev => [...prev, ...results]);
      }
      setHostSearchHasMore(data.hasMore || false);
      setHostSearchPage(page);
    } catch (error) {
      console.error('Error searching hosts:', error);
      if (page === 1) setHostSearchResults([]);
    } finally {
      setHostSearchLoading(false);
    }
  }, []);

  const handleHostScroll = useCallback((e) => {
    const target = e.target;
    // Load more when scrolled to bottom (within 10px)
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
      if (!hostSearchLoading && hostSearchHasMore) {
        searchHosts(hostSearchText, hostSearchPage + 1);
      }
    }
  }, [hostSearchLoading, hostSearchHasMore, hostSearchText, hostSearchPage, searchHosts]);

  const handleHostSearchChange = useCallback((text) => {
    setHostSearchText(text);
    // If the user clears the text, also clear the selected host
    if (!text || text.trim() === '') {
      setFormData((current) => ({ ...current, hostId: '' }));
      setSelectedHostLabel('');
      // Fetch default 20 employees
      searchHosts('', 1);
      return;
    }

    if (hostSearchTimerRef.current) clearTimeout(hostSearchTimerRef.current);
    hostSearchTimerRef.current = setTimeout(() => {
      searchHosts(text, 1);
    }, 400); // 400ms debounce
  }, [searchHosts]);

  // Initial fetch for host search results
  useEffect(() => {
    searchHosts('', 1);
    return () => {
      if (hostSearchTimerRef.current) clearTimeout(hostSearchTimerRef.current);
    };
  }, [searchHosts]);

  // Auto-select host when linked visitor fields are filled (reverse lookup)
  useEffect(() => {
    if (!employees || employees.length === 0 || !formFields || formFields.length === 0) return;

    // Find all visitor fields that have a linkedEmployeeFieldKey and currently have a value
    const linkedFields = formFields.filter(f => !isCoreField(f) && f.enabled && f.linkedEmployeeFieldKey);
    if (linkedFields.length === 0) return;

    // Collect the current values for linked fields
    const filledLinks = linkedFields
      .map(f => ({
        employeeKey: f.linkedEmployeeFieldKey,
        visitorValue: String(customFieldValues[f.key] || '').trim().toLowerCase(),
      }))
      .filter(link => link.visitorValue !== '');

    // Only attempt matching if at least 2 linked fields have values (to avoid false positives)
    if (filledLinks.length < 2) return;

    // Find employees that match ALL filled linked field values
    const matchingEmps = employees.filter(emp => {
      const cf = emp.customFields instanceof Map ? Object.fromEntries(emp.customFields) : (emp.customFields || {});
      const empDataMap = {
        firstName: emp.firstName || '',
        lastName: emp.lastName || '',
        email: emp.email || '',
        phoneNumber: emp.phoneNumber || '',
        department: emp.department || '',
        employeeId: emp.employeeId || '',
        address: emp.address || '',
        ...cf,
      };

      return filledLinks.every(link => {
        const empVal = String(empDataMap[link.employeeKey] || '').trim().toLowerCase();
        return empVal === link.visitorValue;
      });
    });

    if (matchingEmps.length === 1) {
      const emp = matchingEmps[0];
      if (formData.hostId !== emp._id) {
        setFormData(prev => ({ ...prev, hostId: emp._id }));
        const label = buildHostLabel(emp);
        setSelectedHostLabel(label);
        setHostSearchText(label);
        clearFieldError('hostId');
      }
    }
  }, [customFieldValues, employees, formFields, formData.hostId, buildHostLabel]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await apiFetch('/api/employees?all=true');
        const data = await response.json();
        const emps = Array.isArray(data.data) ? data.data : [];
        setEmployees(emps);

        // Map the logged-in User to the corresponding Employee _id
        if (isEmployeeRole(user.role)) {
          const matchedEmployee = emps.find((emp) => {
            const empName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
            const usrName = (user.name || '').trim().toLowerCase();
            const empEmail = (emp.email || '').trim().toLowerCase();
            const usrEmail = (user.email || '').trim().toLowerCase();
            return (usrEmail && empEmail === usrEmail) || (usrName && empName === usrName);
          });
          if (matchedEmployee) {
            setFormData((current) => ({
              ...current,
              hostId: matchedEmployee._id
            }));
            setSelectedHostLabel(buildHostLabel(matchedEmployee));
            setHostSearchText(buildHostLabel(matchedEmployee));
          } else {
            const partialMatch = emps.find((emp) => {
              const firstName = (emp.firstName || '').trim().toLowerCase();
              const usrName = (user.name || '').trim().toLowerCase();
              return firstName && usrName.includes(firstName);
            });
            if (partialMatch) {
              setFormData((current) => ({
                ...current,
                hostId: partialMatch._id
              }));
              setSelectedHostLabel(buildHostLabel(partialMatch));
              setHostSearchText(buildHostLabel(partialMatch));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchFormConfig = async () => {
      try {
        const response = await apiFetch('/api/visitor-fields');
        const data = await response.json();
        if (!response.ok) return;

        const sections = Array.isArray(data.data?.sections)
          ? data.data.sections
            .filter((section) => section.title && section.enabled !== false)
            .sort((left, right) => (left.order || 0) - (right.order || 0))
          : [];
        const fields = Array.isArray(data.data?.fields)
          ? data.data.fields
            .filter((field) => field.enabled)
            .sort((left, right) => (left.order || 0) - (right.order || 0))
          : [];

        if (sections.length > 0) {
          setFormSections(sections);
          setFormFields(fields.map((field) => ({
            ...field,
            sectionId: field.sectionId || sections[0].id,
          })));
          setOptionalSectionExpanded(
            sections.reduce((accumulator, section) => {
              if (section.optional) {
                accumulator[section.id || section._id || 'additional-details'] = false;
              }
              return accumulator;
            }, {})
          );
        } else if (fields.length > 0) {
          setFormSections([createFallbackSection()]);
          setFormFields(fields.map((field) => ({
            ...field,
            sectionId: field.sectionId || 'additional-details',
          })));
        } else {
          setFormSections([]);
          setFormFields([]);
        }
      } catch (error) {
        console.error('Error fetching visitor form fields:', error);
        setFormSections([]);
        setFormFields([]);
      }
    };

    fetchFormConfig();
  }, []);

  useEffect(() => {
    setCustomFieldValues((currentValues) => {
      const nextValues = {};

      formFields
        .filter((field) => !isCoreField(field))
        .forEach((field) => {
          // Default expectedDate and any datetime/date field to current time if not already set
          if (field.key === 'expectedDate' || field.type === FIELD_TYPES.datetime || field.type === FIELD_TYPES.date) {
            nextValues[field.key] = currentValues[field.key] || dayjs().toISOString();
          } else {
            nextValues[field.key] = Object.prototype.hasOwnProperty.call(currentValues, field.key)
              ? currentValues[field.key]
              : buildDefaultValue(field);
          }
        });

      return nextValues;
    });
  }, [formFields]);

  const getSectionFields = (sectionId) => formFields.filter((field) => (field.sectionId || 'additional-details') === sectionId);
  const getSectionIdentifier = (section) => section.id || section._id || 'additional-details';
  const isOptionalSectionExpanded = (section) => {
    if (!section.optional) return true;
    return Boolean(optionalSectionExpanded[getSectionIdentifier(section)]);
  };
  const isFieldInRenderedSection = (field) => {
    const targetSectionId = field.sectionId || 'additional-details';
    return formSections.some((section) => {
      if (getSectionIdentifier(section) !== targetSectionId) return false;
      return isOptionalSectionExpanded(section);
    });
  };
  const getRenderableFields = () => formFields.filter((field) => isFieldVisible(field) && isFieldInRenderedSection(field));

  const clearFieldError = (name) => {
    setErrors((currentErrors) => {
      if (!currentErrors[name]) return currentErrors;

      const updatedErrors = { ...currentErrors };
      delete updatedErrors[name];
      return updatedErrors;
    });
  };

  const getFieldValue = (field) => {
    if (isCoreField(field)) return formData[field.key] || '';
    return customFieldValues[field.key] ?? buildDefaultValue(field);
  };

  const setFieldValue = (field, value) => {
    if (isCoreField(field)) {
      setFormData((currentData) => ({ ...currentData, [field.key]: value }));
    } else {
      setCustomFieldValues((currentValues) => ({
        ...currentValues,
        [field.key]: value,
      }));
    }

    if (Array.isArray(value) ? value.length > 0 : String(value || '').trim()) {
      clearFieldError(field.key);
    }
  };

  const handleHostChange = async (hostId) => {
    setFormData((currentData) => ({ ...currentData, hostId }));
    clearFieldError('hostId');

    const expectedDateVal = customFieldValues.expectedDate;
    if (expectedDateVal && hostId) {
      const date = dayjs(expectedDateVal).tz(TIMEZONE);
      try {
        const dateStr = date.format('YYYY-MM-DD');
        const response = await apiFetch(`/api/availability/host/${hostId}?date=${dateStr}`);
        const payload = await response.json();
        if (response.ok && payload.success) {
          const { workingHours, isWorkingDay, blockedRanges } = payload.data;
          const timeStr = date.format('HH:mm');

          let invalidReason = null;
          if (!isWorkingDay) {
            invalidReason = 'Non-working day (Weekend)';
          } else if (timeStr < workingHours.start || timeStr > workingHours.end) {
            invalidReason = `Outside working hours (${workingHours.start} - ${workingHours.end})`;
          } else {
            const overlappingBlock = blockedRanges.find(range => timeStr >= range.start && timeStr < range.end);
            if (overlappingBlock) {
              invalidReason = overlappingBlock.reason;
            }
          }

          if (invalidReason) {
            message.error(`Selected time is blocked for this host: ${invalidReason}`);
            setCustomFieldValues(prev => ({ ...prev, expectedDate: '' }));
          }
        }
      } catch (err) {
        console.error('Error checking host availability:', err);
      }
    }
  };

  const isFieldVisible = () => true;

  // Get filtered dropdown options for linked fields based on currently filled linked fields
  // E.g., if Block "A" is selected, Flat Numbers dropdown only shows flats from Block A employees
  const getFilteredDropdownOptions = useCallback((field) => {
    const baseOptions = field.options || [];
    // Only filter if this field has a link AND there are employees AND there are other filled linked fields
    if (!field.linkedEmployeeFieldKey || !employees || employees.length === 0) {
      return baseOptions;
    }

    // Get all OTHER linked fields that currently have a value (not this field itself)
    const otherLinkedFields = formFields.filter(
      f => f.key !== field.key && f.linkedEmployeeFieldKey && f.enabled && !isCoreField(f)
    );
    const filledOtherLinks = otherLinkedFields
      .map(f => ({
        employeeKey: f.linkedEmployeeFieldKey,
        visitorValue: String(customFieldValues[f.key] || '').trim().toLowerCase(),
      }))
      .filter(link => link.visitorValue !== '');

    // If no other linked fields are filled, show all options
    if (filledOtherLinks.length === 0) return baseOptions;

    // Find employees matching all the other filled linked fields
    const matchingEmps = employees.filter(emp => {
      const cf = emp.customFields instanceof Map ? Object.fromEntries(emp.customFields) : (emp.customFields || {});
      const empDataMap = {
        firstName: emp.firstName || '', lastName: emp.lastName || '',
        department: emp.department || '', employeeId: emp.employeeId || '',
        ...cf,
      };
      return filledOtherLinks.every(link => {
        const empVal = String(empDataMap[link.employeeKey] || '').trim().toLowerCase();
        return empVal === link.visitorValue;
      });
    });

    // Get the unique values for THIS field's linked employee key from matching employees
    const validValues = new Set();
    matchingEmps.forEach(emp => {
      const cf = emp.customFields instanceof Map ? Object.fromEntries(emp.customFields) : (emp.customFields || {});
      const empDataMap = { department: emp.department || '', ...cf };
      const val = empDataMap[field.linkedEmployeeFieldKey];
      if (val && typeof val === 'string') validValues.add(val.trim());
    });

    // If no valid values found from employees, show all options (don't restrict)
    if (validValues.size === 0) return baseOptions;

    // Filter the dropdown options to only include values that exist in matching employees
    return baseOptions.filter(opt => validValues.has(opt.trim()));
  }, [employees, formFields, customFieldValues]);

  const renderField = (field) => {
    if (!isFieldVisible(field)) return null;

    const fieldError = errors[field.key];
    const fieldValue = getFieldValue(field);
    const translatedLabelText = getTranslatedLabel(field.label, user?.companyName);
    const label = (
      <label className="wm-label">
        {translatedLabelText}
        {field.required && <span className="text-rose-500 ml-1">*</span>}
      </label>
    );

    if (field.key === 'hostId') {
      const isEmployee = isEmployeeRole(user.role);
      // Use paginated search results for the dropdown
      const dataSource = hostSearchResults;
      const hostOptions = dataSource.map((emp) => {
        const hostLabel = buildHostLabel(emp);
        return {
          value: emp._id,
          employeeData: emp, // pass full employee for autofill
          label: (
            <div className="flex items-center gap-2 py-1">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {(emp.firstName || '?')[0]}{(emp.lastName || '?')[0]}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                  {emp.firstName} {emp.lastName}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  {(() => {
                    const cf = emp.customFields instanceof Map ? Object.fromEntries(emp.customFields) : (emp.customFields || {});
                    const parts = [];
                    // Show all custom field values
                    Object.entries(cf).forEach(([, v]) => {
                      if (v && typeof v === 'string' && v.trim()) parts.push(v.trim());
                    });
                    // Fallback to department if no custom fields
                    if (parts.length === 0 && emp.department) parts.push(emp.department);
                    if (emp.employeeId) parts.push(emp.employeeId);
                    return parts.join(' · ');
                  })()}
                </div>
              </div>
            </div>
          ),
          hostLabel, // stored for display after selection
        };
      });

      // If a host is already selected but not in the current paginated options, inject it so it doesn't show the raw ID
      if (formData.hostId && selectedHostLabel && !hostOptions.some(opt => opt.value === formData.hostId)) {
        hostOptions.push({
          value: formData.hostId,
          label: (
            <div className="flex items-center gap-2 py-1">
               <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  {selectedHostLabel}
               </div>
            </div>
          ),
          hostLabel: selectedHostLabel
        });
      }

      // Auto-fill visitor form fields from selected host's data using dynamic field mappings
      const autoFillFromHost = (emp) => {
        if (!emp) return;
        const empCustom = emp.customFields instanceof Map
          ? Object.fromEntries(emp.customFields)
          : (emp.customFields || {});

        // Build a lookup map of ALL the employee's data (core + custom fields)
        const empDataMap = {
          firstName: emp.firstName || '',
          lastName: emp.lastName || '',
          email: emp.email || '',
          phoneNumber: emp.phoneNumber || '',
          department: emp.department || '',
          employeeId: emp.employeeId || '',
          address: emp.address || '',
          ...empCustom,
        };

        // For each visitor form field that has a linkedEmployeeFieldKey, auto-fill it
        setCustomFieldValues((currentValues) => {
          const updated = { ...currentValues };

          formFields.filter(f => !isCoreField(f) && f.enabled && f.linkedEmployeeFieldKey).forEach(visitorField => {
            const empValue = empDataMap[visitorField.linkedEmployeeFieldKey];
            if (empValue !== undefined && empValue !== '') {
              updated[visitorField.key] = empValue;
              clearFieldError(visitorField.key);
            }
          });

          return updated;
        });
      };

      return (
        <div key={field.key} className="space-y-1">
          {label}
          {isEmployee ? (
            // For employee role: show read-only selected host that looks active/identical to other fields
            <Input
              className="h-10 rounded-lg !bg-white dark:!bg-slate-800 !text-slate-900 dark:!text-slate-100 !cursor-not-allowed"
              value={selectedHostLabel || 'You'}
              readOnly
            />
          ) : (
            <Select
              showSearch
              className={`w-full h-10 ${fieldError ? '[&_.ant-select-selector]:!border-red-500' : ''}`}
              options={hostOptions}
              value={formData.hostId || undefined}
              onSearch={handleHostSearchChange}
              onPopupScroll={handleHostScroll}
              onOpenChange={(open) => {
                if (open && !hostSearchText && hostSearchResults.length === 0 && employees.length === 0) {
                  searchHosts('', 1);
                }
              }}
              onSelect={(value, option) => {
                handleHostChange(value);
                setSelectedHostLabel(option.hostLabel);
                setHostSearchText(''); // Clear search text to reset options
                searchHosts('', 1); // Re-fetch defaults
                // Auto-fill other visitor form fields from the host's data
                autoFillFromHost(option.employeeData);
              }}
              onClear={() => {
                setFormData((current) => ({ ...current, hostId: '' }));
                setSelectedHostLabel('');
                setHostSearchText('');
                searchHosts('', 1); // Re-fetch defaults
              }}
              allowClear
              filterOption={false} // We handle filtering via onSearch and backend API
              optionLabelProp="hostLabel"
              placeholder={field.placeholder || `Select or search by name, flat...`}
              popupRender={(menu) => (
                <>
                  {menu}
                  {hostSearchLoading && (
                    <div className="text-center py-3 text-slate-400">
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full" />
                    </div>
                  )}
                </>
              )}
              notFoundContent={
                !hostSearchLoading && hostSearchText && hostSearchText.trim()
                  ? <div className="text-center py-3 text-slate-400 text-sm">No matches found</div>
                  : null
              }
            />
          )}
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.key === 'expectedDate' || field.type === FIELD_TYPES.date || field.type === FIELD_TYPES.datetime) {
      const showNotes = isReceptionist && field.key === 'expectedDate';
      const isDateTime = field.type === FIELD_TYPES.datetime || field.key === 'expectedDate';

      return (
        <div key={field.key} className="space-y-1">
          {label}
          <DatePicker
            className={`w-full h-10 ${fieldError ? '[&_.ant-picker]:border-red-500' : ''}`}
            format={isDateTime ? "DD-MM-YYYY HH:mm" : "DD-MM-YYYY"}
            showTime={isDateTime ? { defaultValue: dayjs(), format: 'HH:mm', minuteStep: 5 } : false}
            placeholder={field.placeholder || (isDateTime ? 'Select date and time' : 'Select date')}
            value={fieldValue ? dayjs(fieldValue) : null}
            disabledDate={(current) => {
              if (isReceptionist && field.key === 'expectedDate') {
                return current && !current.isSame(dayjs(), 'day');
              }
              return current && current.isBefore(dayjs().startOf('day'));
            }}
            disabledTime={(current) => {
              if (isReceptionist && field.key === 'expectedDate') {
                return {};
              }
              if (current && current.isSame(dayjs(), 'day')) {
                return {
                  disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h < dayjs().hour()),
                  disabledMinutes: () => current.hour() === dayjs().hour() ? Array.from({ length: 60 }, (_, i) => i).filter(m => m < dayjs().minute()) : [],
                  disabledSeconds: () => []
                };
              }
              return {};
            }}
            onChange={async (date) => {
              if (!date) {
                setFieldValue(field, null);
                return;
              }

              // Validate past date or time without resetting to null
              if (!(isReceptionist && field.key === 'expectedDate')) {
                const limit = isDateTime ? dayjs().subtract(5, 'minute').startOf('minute') : dayjs().startOf('day');
                if (date.isBefore(limit)) {
                  const errMsg = isDateTime ? 'Cannot select a past date or time' : 'Cannot select a past date';
                  message.error(errMsg);
                  setErrors((prev) => ({ ...prev, [field.key]: errMsg }));
                  setFieldValue(field, date.toISOString());
                  return;
                }
              }

              // Clear past check error
              clearFieldError(field.key);

              // Check availability
              if (formData.hostId && field.key === 'expectedDate') {
                try {
                  const dateStr = date.tz(TIMEZONE).format('YYYY-MM-DD');
                  const response = await apiFetch(`/api/availability/host/${formData.hostId}?date=${dateStr}`);
                  const payload = await response.json();
                  if (response.ok && payload.success) {
                    const { workingHours, isWorkingDay, blockedRanges } = payload.data;
                    const timeStr = date.tz(TIMEZONE).format('HH:mm');

                    let invalidReason = null;
                    if (!isWorkingDay) {
                      invalidReason = 'Non-working day (Weekend)';
                    } else if (timeStr < workingHours.start || timeStr > workingHours.end) {
                      invalidReason = `Outside working hours (${workingHours.start} - ${workingHours.end})`;
                    } else {
                      const overlappingBlock = blockedRanges.find(range => timeStr >= range.start && timeStr < range.end);
                      if (overlappingBlock) {
                        invalidReason = overlappingBlock.reason;
                      }
                    }

                    if (invalidReason) {
                      message.error(`Cannot book: ${invalidReason}`);
                      setErrors((prev) => ({ ...prev, [field.key]: `Cannot book: ${invalidReason}` }));
                      setFieldValue(field, date.toISOString());
                      return;
                    }
                  }
                } catch (err) {
                  console.error('Error checking availability:', err);
                }
              }

              setFieldValue(field, date ? date.toISOString() : '');
            }}
          />
          {showNotes && <div className="text-[11px] text-slate-400">Receptionists can only register visitors for today.</div>}
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.key === 'email') {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <Input
            size="middle"
            type="email"
            autoComplete="new-password"
            value={fieldValue}
            onChange={(event) => {
              const val = event.target.value;
              setFieldValue(field, val);
              lookupVisitorByContact(val, formData.mobileNumber);
            }}
            onBlur={() => lookupVisitorByContact(fieldValue, formData.mobileNumber)}
            className={`rounded-lg h-10 ${fieldError ? 'border-red-500' : ''}`}
            placeholder={field.placeholder || `Enter ${translatedLabelText}`}
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.key === 'mobileNumber') {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <PhoneInput
            country={'in'}
            value={fieldValue}
            onChange={(phone) => {
              const val = '+' + phone;
              setFieldValue(field, val);
              lookupVisitorByContact(formData.email, val);
            }}
            onBlur={() => lookupVisitorByContact(formData.email, fieldValue)}
            inputProps={{ autoComplete: 'new-password' }}
            inputClass={`!w-full !h-10 !rounded-lg !border-slate-200 dark:!bg-slate-800 dark:!border-slate-700 dark:!text-white ${fieldError ? '!border-red-500' : ''}`}
            buttonClass="dark:!bg-slate-800 dark:!border-slate-700"
            dropdownClass="dark:!bg-slate-800 dark:!text-white"
            placeholder={field.placeholder || `Enter ${translatedLabelText}`}
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.type === FIELD_TYPES.dropdown) {
      const isPurpose = field.key === 'purpose';
      const PURPOSE_DEFAULTS = ['Meeting', 'Interview'];
      // For linked fields, filter options based on other filled linked fields
      const filteredOptions = field.linkedEmployeeFieldKey
        ? getFilteredDropdownOptions(field)
        : (field.options || []);
      const baseOptions = isPurpose ? (field.options?.length ? field.options : PURPOSE_DEFAULTS) : filteredOptions;
      const allOptions = isPurpose
        ? [...normalizeOptions(baseOptions), { value: '__other__', label: 'Other' }]
        : normalizeOptions(baseOptions);

      // For purpose field, store selected dropdown key separately
      const rawSelected = isPurpose
        ? (customFieldValues['__purpose_select__'] ?? (
          fieldValue && !baseOptions.includes(fieldValue) && fieldValue !== '__other__'
            ? '__other__'
            : fieldValue || undefined
        ))
        : fieldValue || undefined;
      const isOther = isPurpose && rawSelected === '__other__';

      const handlePurposeSelect = (value) => {
        if (value === '__other__') {
          setCustomFieldValues((cv) => ({ ...cv, __purpose_select__: '__other__', purpose: '' }));
        } else {
          setCustomFieldValues((cv) => { const n = { ...cv, __purpose_select__: value, purpose: value }; return n; });
          clearFieldError(field.key);
        }
      };

      return (
        <div key={field.key} className="space-y-1">
          {label}
          <Select
            className={`w-full h-10 ${fieldError ? 'border-red-500' : ''}`}
            placeholder={field.placeholder || `Select ${translatedLabelText}`}
            value={isPurpose ? rawSelected : (fieldValue || undefined)}
            onChange={isPurpose ? handlePurposeSelect : (value) => setFieldValue(field, value)}
            options={allOptions}
            showSearch={!!field.linkedEmployeeFieldKey}
            allowClear={!!field.linkedEmployeeFieldKey}
            optionFilterProp="label"
          />
          {isOther && (
            <div className="pt-2 pb-1">
              <Input
                size="middle"
                autoFocus
                placeholder="Specify purpose..."
                value={fieldValue}
                onChange={(e) => {
                  setCustomFieldValues((cv) => ({ ...cv, purpose: e.target.value }));
                  if (e.target.value.trim()) clearFieldError(field.key);
                }}
                className={`rounded-lg h-10 ${fieldError ? 'border-red-500' : ''}`}
              />
            </div>
          )}
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.type === FIELD_TYPES.checklist) {
      return (
        <div key={field.key} className="space-y-1 md:col-span-2">
          {label}
          <Checkbox.Group
            className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3"
            options={normalizeOptions(field.options)}
            value={fieldValue || []}
            onChange={(values) => setFieldValue(field, values)}
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.key === 'firstName' || field.key === 'lastName') {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <AutoComplete
            className="w-full"
            options={visitorNameSuggestions}
            onSearch={searchPastVisitors}
            onSelect={(val, option) => {
              applyVisitorFromLookup(option.data);
            }}
            value={fieldValue}
            onChange={(val) => setFieldValue(field, val)}
          >
            <Input
              size="middle"
              autoComplete="new-password"
              className={`rounded-lg h-10 ${fieldError ? 'border-red-500' : ''}`}
              placeholder={field.placeholder || `Enter ${translatedLabelText}`}
            />
          </AutoComplete>
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    return (
      <div key={field.key} className="space-y-1">
        {label}
        <Input
          size="middle"
          type="text"
          autoComplete="new-password"
          value={fieldValue}
          onChange={(event) => setFieldValue(field, event.target.value)}
          className={`rounded-lg h-10 ${fieldError ? 'border-red-500' : ''}`}
          placeholder={field.placeholder || `Enter ${translatedLabelText}`}
        />
        {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
      </div>
    );
  };

  const validateFields = () => {
    const newErrors = {};

    getRenderableFields().forEach((field) => {
      const value = getFieldValue(field);
      const hasTextValue = typeof value === 'string' && value.trim().length > 0;
      const hasChecklistValue = Array.isArray(value) && value.length > 0;
      const hasValue = hasTextValue || hasChecklistValue || (value !== null && value !== undefined && value !== '');

      if (field.required && !hasValue) {
        newErrors[field.key] = `${field.label} is required`;
      }

      if (field.key === 'email' && hasTextValue && !isValidEmail(value)) {
        newErrors.email = 'Please enter a valid Email';
      }

      if (field.key === 'mobileNumber' && hasTextValue && value.length < 10) {
        newErrors.mobileNumber = 'Please enter a valid phone number';
      }

      if ((field.type === FIELD_TYPES.date || field.type === FIELD_TYPES.datetime || field.key === 'expectedDate') && hasValue) {
        const chosen = dayjs(value);
        const isDateTime = field.type === FIELD_TYPES.datetime || field.key === 'expectedDate';
        if (!isDateTime) {
          // Pure date: check if it's before today (at day level)
          if (chosen.isBefore(dayjs(), 'day')) {
            newErrors[field.key] = 'Cannot select a past date';
          }
        } else {
          // Datetime or expectedDate: check if it's before current minute (allow 5 min grace period for "Now" selections)
          if (chosen.isBefore(dayjs().subtract(5, 'minute'), 'minute')) {
            newErrors[field.key] = 'Cannot select a past date or time';
          }
        }
      }
    });

    return newErrors;
  };

  const handleRegister = async (event) => {
    if (event) event.preventDefault();

    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      console.log('Validation Errors:', newErrors);
      message.error('Please fill all required fields correctly.');
      setErrors(newErrors);
      return;
    }

    const customFields = formFields
      .filter((field) => !isCoreField(field))
      .reduce((accumulator, field) => {
        accumulator[field.key] = getFieldValue(field);
        return accumulator;
      }, {});

    const visitPurpose = customFields.purpose || '';
    const expectedDate = customFields.expectedDate 
      || customFields.visitDate 
      || Object.values(customFields).find(val => typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))) 
      || dayjs().toISOString();

    setErrors({});
    setIsSubmitting(true);

    // Perform host availability check on submit
    if (formData.hostId) {
      try {
        const dateObj = dayjs(expectedDate).tz(TIMEZONE);
        const dateStr = dateObj.format('YYYY-MM-DD');
        const response = await apiFetch(`/api/availability/host/${formData.hostId}?date=${dateStr}`);
        const payload = await response.json();
        if (response.ok && payload.success) {
          const { workingHours, isWorkingDay, blockedRanges, meetingDuration } = payload.data;
          const timeStr = dateObj.format('HH:mm');
          const proposedEndStr = dateObj.add(meetingDuration !== undefined ? meetingDuration : 0, 'minute').format('HH:mm');

          let invalidReason = null;
          if (!isWorkingDay) {
            invalidReason = 'Non-working day (Weekend)';
          } else if (timeStr < workingHours.start || timeStr >= workingHours.end) {
            invalidReason = `Outside working hours (${workingHours.start} - ${workingHours.end})`;
          } else {
            const overlappingBlock = blockedRanges.find(range => {
              const start = range.start;
              const end = range.end;
              return timeStr < end && proposedEndStr > start;
            });
            if (overlappingBlock) {
              invalidReason = overlappingBlock.reason;
            }
          }

          if (invalidReason) {
            message.error(`Cannot register visitor: Host is busy/blocked (${invalidReason})`);
            setIsSubmitting(false);
            return;
          }
        }
      } catch (err) {
        console.error('Error checking host availability during submission:', err);
      }
    }

    try {
      const visitorResponse = await apiFetch('/api/visitors', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          mobileNumber: formData.mobileNumber,
          hostId: formData.hostId,
          purpose: visitPurpose,
          address: {
            line1: customFields.addr_line1 || customFields.address || '',
            city: customFields.addr_city || customFields.city || '',
            state: customFields.addr_state || customFields.state || '',
          },
          customFields,
          photo: photo || undefined,
          createdByRole: user.role
        }),
      });

      const visitorData = await visitorResponse.json();

      if (!visitorResponse.ok) {
        message.error(visitorData.message || 'Registration failed');
        return;
      }

      if (formData.hostId) {
        if (!visitPurpose) {
          message.warning('Please add a Purpose field to the form or enter a purpose for the visit');
          return;
        }
        const visitBody = {
          visitor: visitorData.data._id,
          host: formData.hostId,
          purpose: visitPurpose,
          expectedDate,
          createdByRole: user.role,
        };

        if (isReceptionist) {
          visitBody.status = 'pending';
        } else if (isStaffRegistrar) {
          visitBody.status = 'approved';
          visitBody.preApproved = true;
        } else {
          visitBody.status = 'pending';
        }

        const visitResponse = await apiFetch('/api/visits', {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(visitBody),
        });

        if (!visitResponse.ok) {
          const visitError = await visitResponse.json().catch(() => ({}));
          if (visitError.isAlreadyPreApproved) {
            Modal.info({
              title: 'Visitor Already Pre-approved',
              content: visitError.message || 'This visitor is already in the pre-approved list for this time slot.',
              onOk() {
                navigate('/visit-requests?tab=Pre-approved+visitor');
              }
            });
            setIsSubmitting(false);
            return;
          }
          message.error(visitError.message || 'Visitor saved, but visit registration failed.');
          return;
        }
      }

      message.success('Visitor registered successfully!');
      setStep(2);
    } catch (error) {
      console.error("Registration Error:", error);
      message.error("Error connecting to server");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(createInitialFormData());
    setErrors({});
    setPhoto(null);
    stopCamera();
    lastLookupKeyRef.current = '';
    setHostSearchText('');
    setHostSearchResults([]);
    setSelectedHostLabel('');
    setCustomFieldValues(
      formFields
        .filter((field) => !isCoreField(field))
        .reduce((accumulator, field) => {
          accumulator[field.key] = buildDefaultValue(field);
          return accumulator;
        }, {})
    );
    setOptionalSectionExpanded(
      formSections.reduce((accumulator, section) => {
        if (section.optional) {
          accumulator[getSectionIdentifier(section)] = false;
        }
        return accumulator;
      }, {})
    );
    setStep(1);
  };

  return (
    <div className="wm-page">
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px #dbeafe inset !important;
          -webkit-text-fill-color: #1f2937 !important;
        }
      `}</style>
      <div className="wm-card-header wm-card-flat dark:bg-vms-navy dark:border-white/10">
        <div>
          <h1 className="wm-page-title text-xl dark:text-white">Register {labels.visitor}</h1>
        </div>
      </div>

      <div className="wm-card-flat dark:bg-vms-navy dark:border-white/10 overflow-hidden">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.form
              key="step1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              onSubmit={handleRegister}
              autoComplete="off"
              noValidate
              className="px-6 py-5"
            >
              <div className="space-y-6">
                {formSections.map((section) => {
                  const sectionId = getSectionIdentifier(section);
                  const sectionFields = getSectionFields(sectionId).filter(isFieldVisible);
                  const isOptional = Boolean(section.optional);
                  const isExpanded = isOptionalSectionExpanded(section);

                  if (!isOptional && sectionFields.length === 0) return null;

                  return (
                    <div key={sectionId} className="space-y-3">
                      <div className="flex items-center gap-3">
                        {isOptional ? (
                          <Switch
                            checked={isExpanded}
                            onChange={(checked) => {
                              setOptionalSectionExpanded((current) => ({
                                ...current,
                                [sectionId]: checked,
                              }));
                              if (!checked) {
                                sectionFields.forEach((field) => clearFieldError(field.key));
                              }
                            }}
                            aria-label={`Show ${section.title} fields`}
                          />
                        ) : null}
                        <h3 className="wm-section-title whitespace-nowrap">{section.title}</h3>
                        {isOptional ? (
                          <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">(Optional)</span>
                        ) : null}
                        <div className="h-px bg-slate-100 dark:bg-white/5 w-full"></div>
                      </div>

                      {isExpanded && sectionFields.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          {sectionFields.map((field) => renderField(field))}
                        </div>
                      ) : null}

                      {isExpanded && sectionFields.length === 0 ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">No fields configured for this section.</p>
                      ) : null}

                      {isOptional && !isExpanded ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Turn on the switch above to add {section.title.toLowerCase()} details.
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {isReceptionist && (
                <div className="mt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="wm-section-title whitespace-nowrap">{labels.visitor} Photo</h3>
                    <div className="h-px bg-slate-100 dark:bg-white/5 w-full"></div>
                  </div>

                  <div className="flex flex-col items-center gap-3 w-full max-w-sm mx-auto px-2 sm:px-0">
                    {cameraActive && !photo && (
                      <div className="relative w-full flex flex-col items-center">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          onLoadedMetadata={() => setVideoReady(true)}
                          className={`${VISITOR_PHOTO_BOX_CLASS} bg-black`}
                        />
                        <div className="flex gap-2 mt-2 justify-center">
                          <Button onClick={capturePhoto} type="primary" disabled={!videoReady} className="wm-btn-primary">
                            Capture
                          </Button>
                          <Button onClick={stopCamera} className="text-slate-500">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {photo && (
                      <div className="flex flex-col items-center gap-2">
                        <img
                          src={photo}
                          alt={labels.visitor}
                          className={VISITOR_PHOTO_BOX_CLASS}
                        />
                        <Button onClick={retakePhoto} size="small" className="text-slate-500 text-xs">
                          Retake Photo
                        </Button>
                      </div>
                    )}

                    {!cameraActive && !photo && (
                      <button
                        type="button"
                        onClick={startCamera}
                        className={`${VISITOR_PHOTO_BOX_CLASS} border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500 hover:border-blue-400 hover:text-blue-400 transition-colors cursor-pointer bg-slate-50 dark:bg-slate-800 object-none`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm font-medium">Open Camera</span>
                      </button>
                    )}
                    {errors.photo && <div className="text-[11px] text-red-500">{errors.photo}</div>}
                  </div>
                </div>
              )}

              <div className="mt-8 flex justify-end pb-2">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  className="wm-btn-primary px-8 h-10"
                >
                  Save {labels.visitor}
                </Button>
              </div>
            </motion.form>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-24 text-center p-8"
            >
              <div className="w-24 h-24 bg-emerald-50 dark:bg-emerald-900/30 rounded-[2rem] flex items-center justify-center mb-8 shadow-inner text-emerald-600 dark:text-emerald-400">
                <FiCheck className="w-12 h-12" />
              </div>
              <h3 className="text-4xl font-semibold text-slate-800 dark:text-white mb-4 tracking-tight">{labels.visitor} Added!</h3>
              <p className="text-slate-500 mb-12 max-w-sm font-medium">The {labels.visitor.toLowerCase()} has been successfully registered in the system.</p>

              <Button
                type="primary"
                size="large"
                onClick={resetForm}
                className="wm-btn-primary w-full max-w-xs h-12"
              >
                Add Another {labels.visitor}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RegisterVisitor;
